import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";
import { toast } from "@/hooks/use-toast";
import { useRecorderNoteSettings } from "@/hooks/visits/useRecorderNoteSettings";
import { changePreChartTemplate } from "@/lib/pre-chart";
import {
  type Examination,
  toRecorderFormData,
} from "@/lib/recorder-note-settings";
import { useOptionalTemplateList } from "@/lib/template-queries";
import { refreshVisitReads } from "@/lib/visit-queries";
import { useAuthStore } from "@/store/auth.store";
import { usePreChartStore } from "@/store/preChart.store";
import { RecorderTemplateChip } from "./RecorderTemplateChip";

// The draft's template as a template chip value: a built-in name or the
// custom template's public id.
export const preChartTemplateValue = (file: {
  template: string | null;
  customTemplatePublicId: string | null;
}): string | undefined =>
  file.template === "Custom"
    ? (file.customTemplatePublicId ?? undefined)
    : (file.template ?? undefined);

/**
 * A pre-chart draft's template, changeable (clinician feedback 03/11; it was
 * locked). Picking another template asks first, then saves it on the visit
 * and has the assistant rewrite the draft under the new template's headings,
 * keeping its content. That rewrite is reviewed like any assistant edit
 * (Apply, then Undo).
 */
export const PreChartTemplateChip = ({
  file,
  className,
}: {
  file: {
    public_id: string;
    template: string | null;
    customTemplatePublicId: string | null;
    exam: string | null;
  };
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const current = preChartTemplateValue(file);
  const settings = useRecorderNoteSettings(current);
  const { templates } = useOptionalTemplateList(true);
  const uid = useAuthStore((state) => state.user?.uid);
  const userData = useAuthStore((state) => state.userData);
  const examination: Examination =
    file.exam === "Virtual" ? "Virtual" : "Physical";
  const [pending, setPending] = useState<{
    template: string;
    examination: Examination;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const nameOf = (value: string): string =>
    [...settings.favorites, ...settings.builtIn, ...settings.custom].find(
      (choice) => choice.value === value,
    )?.name ?? value;

  const save = async (choice: {
    template: string;
    examination: Examination;
  }): Promise<void> => {
    const values = toRecorderFormData({
      template: choice.template,
      examination: choice.examination,
      templates,
      style: userData?.style,
      format: userData?.format,
    });
    if (!values || !uid) return;
    setSaving(true);
    try {
      const headings = await changePreChartTemplate(
        file.public_id,
        values,
        uid,
      );
      void refreshVisitReads();
      // Only the examination changed: same headings, nothing to rewrite.
      if (choice.template !== current)
        usePreChartStore.getState().requestTemplateRewrite(
          file.public_id,
          t("preChart.template.rewriteInstruction", {
            headings: headings.join(", "),
          }),
        );
      setPending(null);
    } catch {
      toast({ variant: "error", title: t("preChart.template.failed") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <RecorderTemplateChip
        className={className}
        settings={{
          ...settings,
          template: current ?? settings.template,
          templateName: current ? nameOf(current) : settings.templateName,
          examination,
          setTemplate: (value) => {
            if (value !== current) setPending({ template: value, examination });
          },
          setExamination: (value) => {
            if (value !== examination && current)
              void save({ template: current, examination: value });
          },
        }}
      />
      <V3ConfirmSheet
        busy={saving}
        confirmLabel={t("preChart.template.confirm")}
        description={t("preChart.template.description")}
        open={!!pending}
        stayOpen
        title={t("preChart.template.title", {
          name: pending ? nameOf(pending.template) : "",
        })}
        onConfirm={() => {
          if (pending) void save(pending);
        }}
        onOpenChange={(open) => {
          if (!open && !saving) setPending(null);
        }}
      />
    </>
  );
};
